/**
 * Pluggable conflict resolution.
 *
 * A conflict arises when the server rejects an outbox item because the local
 * mutation was based on a stale `__version` (optimistic-concurrency miss). The
 * policy decides what to do with the *local* intent:
 *
 *  - **server-authoritative** (default): the server's state is canonical;
 *    discard the local mutation. Correct for append-only ledgers and any data
 *    model where the server is the single source of truth.
 *  - **client-wins**: re-enqueue rebased on the server's `__version` so the
 *    local change overwrites the server. Use only when the client genuinely
 *    owns the field.
 *  - **lww** (last-write-wins): compare timestamps — the most recently updated
 *    side wins. Falls back to server-authoritative when timestamps are absent.
 *  - **manual**: never auto-resolve; surface to `useConflicts()` for the app
 *    (or the user) to decide.
 */

import type {
	ConflictPolicy,
	ConflictPolicyName,
	ConflictResolution,
	SyncConflict,
} from './types'
import { validateResolveConflictPolicySpec } from './validate-input'

function readUpdatedAt(row: unknown): number | null {
	if (row && typeof row === 'object' && '__updatedAt' in row) {
		const v = (row as { __updatedAt?: unknown }).__updatedAt
		if (typeof v === 'number' && Number.isFinite(v)) return v
		if (typeof v === 'string') {
			const parsed = Date.parse(v)
			return Number.isNaN(parsed) ? null : parsed
		}
	}
	return null
}

const serverAuthoritative: ConflictPolicy = (): ConflictResolution => ({
	action: 'discard',
})

const clientWins: ConflictPolicy = (c: SyncConflict): ConflictResolution => ({
	action: 'retry',
	rebasedVersion: c.serverVersion,
})

const lww: ConflictPolicy = (c: SyncConflict): ConflictResolution => {
	const serverAt = readUpdatedAt(c.serverRow)
	const localAt = readUpdatedAt(c.entry.payload)
	if (localAt !== null && serverAt !== null) {
		return localAt > serverAt
			? { action: 'retry', rebasedVersion: c.serverVersion }
			: { action: 'discard' }
	}
	// Insufficient information → safest default is server-authoritative.
	return { action: 'discard' }
}

const manual: ConflictPolicy = (): ConflictResolution => ({ action: 'manual' })

const REGISTRY: Record<ConflictPolicyName, ConflictPolicy> = {
	'server-authoritative': serverAuthoritative,
	'client-wins': clientWins,
	lww,
	manual,
}

/**
 * Resolve a policy spec (a named built-in or a custom function) into a
 * {@link ConflictPolicy}. Unknown names fall back to `server-authoritative`.
 */
export function resolveConflictPolicy(
	spec: ConflictPolicyName | ConflictPolicy | undefined,
): ConflictPolicy {
	// Wave 10 zod-mandate — runtime validation at the public boundary.
	// `spec` may be a name, function, or undefined; the schema is a union.
	validateResolveConflictPolicySpec(spec)
	if (typeof spec === 'function') return spec
	if (spec && spec in REGISTRY) return REGISTRY[spec]
	return REGISTRY['server-authoritative']
}

export { serverAuthoritative, clientWins, lww, manual }
